import _ from 'lodash'

import React from 'react'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { Transaction } from '../../api'
import {
  classifyTaprootWitness,
  parseTaprootOutputFields,
  TaprootWitnessField,
  TaprootWitnessRole
} from './TaprootRawFields'

interface TransactionRawComponentProps {
  transaction: Transaction
}

interface RawFieldProps {
  className: string
  title: string
  value: string
}

const RawField = ({ className, title, value }: RawFieldProps) => (
  <Tooltip title={title} arrow>
    <span className={`TxRawInteractive ${className}`} tabIndex={0} aria-label={title}>
      {value}
    </span>
  </Tooltip>
)

const witnessRoleDetails = (
  field: TaprootWitnessField,
  fields: TaprootWitnessField[],
  inputIndex: number,
  witnessIndex: number
): { className: string; title: string } => {
  const details: Record<TaprootWitnessRole, { className: string; title: string }> = {
    'key-path-signature': {
      className: 'TxTaprootKeyPathSignature',
      title: `Taproot key-path signature for input ${inputIndex}`
    },
    'script-path-stack-argument': {
      className: 'TxTaprootStackArgument',
      title: `Taproot script-path stack argument ${
        fields.slice(0, witnessIndex).filter(({ role }) => role === 'script-path-stack-argument').length
      } for input ${inputIndex}`
    },
    tapscript: {
      className: 'TxTaprootTapscript',
      title: `Tapscript for Taproot input ${inputIndex}`
    },
    'control-block': {
      className: 'TxTaprootControlBlock',
      title: `Control block for Taproot input ${inputIndex}`
    },
    annex: {
      className: 'TxTaprootAnnex',
      title: `Taproot annex for input ${inputIndex}`
    }
  }

  return details[field.role]
}

export const TransactionRawComponent: React.FunctionComponent<TransactionRawComponentProps> = (props) => {
  const { transaction } = props
  const txRaw = transaction.txRaw

  return txRaw ? (
    <div style={{ maxWidth: '480px', textAlign: 'left', marginTop: '20px', wordWrap: 'break-word' }}>
      <Typography variant="body2">
        <Tooltip title="Version" arrow>
          <span className="TxVersion">{txRaw.version}</span>
        </Tooltip>
        {txRaw.flag ? (
          <Tooltip title="Flag" arrow>
            <span className="TxFlag">{txRaw.flag}</span>
          </Tooltip>
        ) : undefined}
        {
          <Tooltip title="Number of transaction inputs" arrow>
            <span className="TxListCount">{txRaw.txIns.count}</span>
          </Tooltip>
        }
        {_.map(txRaw.txIns.txIns, (txIn, index) => {
          return (
            <span key={index}>
              <Tooltip title={`Spent transaction id for input ${index}`} arrow>
                <span className="TxInputPreviousTxId">{txIn.previousOutput.hash}</span>
              </Tooltip>
              <Tooltip title={`Spent transaction output index for input ${index}`} arrow>
                <span className="TxInputPreviousTxOutput">{txIn.previousOutput.index}</span>
              </Tooltip>
              <Tooltip title={`Unlocking script for input ${index}`} arrow>
                <span className="TxInputUnLockingScript">{txIn.sigScript}</span>
              </Tooltip>
              <Tooltip title={`Sequence number for input ${index}`} arrow>
                <span className="TxInputSequence">{txIn.sequence}</span>
              </Tooltip>
            </span>
          )
        })}
        {
          <Tooltip title="Number of transaction outputs" arrow>
            <span className="TxListCount">{txRaw.txOuts.count}</span>
          </Tooltip>
        }
        {_.map(txRaw.txOuts.txOuts, (txOut, index) => {
          const taprootFields =
            _.nth(transaction.outputs, index)?.scriptType === 'pay-to-taproot'
              ? parseTaprootOutputFields(txOut.pkScript)
              : undefined

          return (
            <span key={index}>
              <Tooltip title={`Value of output ${index}`} arrow>
                <span className="TxOutputValue">{txOut.value}</span>
              </Tooltip>
              {taprootFields ? (
                <>
                  <RawField
                    className="TxTaprootScriptLength"
                    title={`CompactSize script length for Taproot output ${index}: 34 bytes`}
                    value={taprootFields.compactSize}
                  />
                  <RawField
                    className="TxTaprootWitnessVersion"
                    title={`Witness version 1 for Taproot output ${index} (OP_1)`}
                    value={taprootFields.witnessVersion}
                  />
                  <RawField
                    className="TxTaprootPushLength"
                    title={`Push 32 bytes for Taproot output ${index}`}
                    value={taprootFields.pushLength}
                  />
                  <RawField
                    className="TxTaprootOutputKey"
                    title={`Taproot output key for output ${index} (32-byte x-only public key)`}
                    value={taprootFields.outputKey}
                  />
                </>
              ) : (
                <Tooltip title={`Locking script for output ${index}`} arrow>
                  <span className="TxOutputLockingScript">{txOut.pkScript}</span>
                </Tooltip>
              )}
            </span>
          )
        })}
        {_.map(txRaw.txWitnesses, (txWitnesses, inputIndex) => {
          const taprootWitness =
            _.nth(transaction.inputs, inputIndex)?.scriptType === 'pay-to-taproot'
              ? classifyTaprootWitness(txWitnesses.txWitnesses.map(({ witness }) => witness))
              : undefined

          return (
            <span key={inputIndex}>
              <Tooltip title={`Number of witnesses for input ${inputIndex}`} arrow>
                <span className="TxListCount">{txWitnesses.count}</span>
              </Tooltip>
              {taprootWitness
                ? taprootWitness.map((field, witnessIndex) => {
                    const details = witnessRoleDetails(field, taprootWitness, inputIndex, witnessIndex)
                    return (
                      <React.Fragment key={witnessIndex}>
                        <RawField
                          className="TxTaprootWitnessLength"
                          title={`CompactSize length for ${details.title.toLowerCase()}: ${field.payloadLength} bytes`}
                          value={field.compactSize}
                        />
                        <RawField className={details.className} title={details.title} value={field.payload} />
                      </React.Fragment>
                    )
                  })
                : _.map(txWitnesses.txWitnesses, (txWitness, witnessIndex) => {
                    return (
                      <Tooltip key={witnessIndex} title={`Witness ${witnessIndex} for input ${inputIndex}`} arrow>
                        <span className={`TxWitness${witnessIndex % 2}`}>{txWitness.witness}</span>
                      </Tooltip>
                    )
                  })}
            </span>
          )
        })}
        {
          <Tooltip title="Locking time" arrow>
            <span className="TxLockTime">{txRaw.lockTime}</span>
          </Tooltip>
        }
      </Typography>
    </div>
  ) : (
    <div> Transaction raw data not available </div>
  )
}

export default TransactionRawComponent
